// src/context/UserContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
// Api
import client from '../api/client';
// Constants
import { GET_LOGGED_IN_USER_API } from '../utils/ApiRoutes';
import { HOME_PAGE_URL } from '../utils/Routes';
// Token utils (session first, then local)
import { getToken, removeToken } from '../utils/user/token';
// Authz utils
import {
  ROLES,
  hasRole,
  hasPermission,
  isActive,
} from '../utils/user/Permissions';
// Nav
import useNavigateToPage from '../hooks/useNavigateToPage';

export const UserContext = createContext();

const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getToken());
  const [loading, setLoading] = useState(true);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const navigateToPage = useNavigateToPage();

  // Mounted guard
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // On 401, clear storage + context token
  const handleAuthError = useCallback(() => {
    try {
      removeToken();
    } catch {}
    setToken(null);
  }, []);

  // Fetch current user
  const fetchMe = useCallback(() => {
    setLoading(true);

    client
      .get(GET_LOGGED_IN_USER_API, true)
      .then((res) => {
        if (!isMountedRef.current) return;
        setUser(res?.data?.user ?? null);
        setLastFetchedAt(Date.now());
        setLoading(false);
      })
      .catch((err) => {
        if (!isMountedRef.current) return;
        if (err?.response?.status === 401) {
          handleAuthError();
        }
        setUser(null);
        setLastFetchedAt(null);
        setLoading(false);
      });
  }, [handleAuthError]);

  // Rehydrate whenever token changes (no decode gate)
  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    fetchMe();
  }, [token, fetchMe]);

  // Manual refresh
  const refreshUser = useCallback(() => {
    if (!token) return;
    fetchMe();
  }, [token, fetchMe]);

  // Logout
  const logout = useCallback(() => {
    try {
      removeToken(); // clears session & local
    } catch {}
    setToken(null);
    setUser(null);
    setLastFetchedAt(null);
    navigateToPage(HOME_PAGE_URL, { replace: true });
  }, [navigateToPage]);

  // Authorization helpers (null-safe)
  const authz = useMemo(
    () => ({
      isAuthenticated: !!user,
      isActive: isActive(user),
      isAdmin: hasRole(user, ROLES.ADMIN),
      isDeveloper: hasRole(user, ROLES.DEVELOPER),
      hasRole: (...roles) => hasRole(user, ...roles),
      can: (perm) => hasPermission(user, perm),
    }),
    [user]
  );

  // Context value
  const value = useMemo(
    () => ({
      // state
      user,
      token,
      loading,
      isReady: !loading,
      lastFetchedAt,

      // setters
      setUser,
      setToken,

      // authz helpers
      ...authz,

      // actions
      refreshUser,
      logout,
    }),
    [user, token, loading, lastFetchedAt, authz, refreshUser, logout]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserProvider;
